import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  IconButton,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  EditOutlined,
  ListAlt,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import ViewUserManagement from "./ViewUser";
import { Loader } from "../../components/CustomLoader";
import { renderEllipsisText } from "../../utils/cssStyles";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";

export default function UserManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, id: "", currentStatus: "" });

  const fetchUsers = async (query = "") => {
    try {
      setIsLoading(true);
      const body = {
        searchItem: query,
        pageNumber: currentPage,
        pageSize,
        status,
      };
      const res = await userServices.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.data);
          setTotalPages(res?.data?.data?.totalDocs);
        }
        setLoaderDelay(true);
      }, 400);
    } catch (err) {
      setData([]);
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce(fetchUsers, 400), [currentPage, pageSize, status]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteUser = async (id) => {
    const result = await Swal.fire({
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        const res = await userServices.deleteUser({ id });
        if (res?.status === 200) {
          toast.success(res.data.message);
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message);
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const toggleStatus = async () => {
    const { id, currentStatus } = statusDialog;
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const res = await userServices.changeStatus({ id, status: newStatus });
      if (res?.status === 200) {
        toast.success(res.data.message);
        setRefresh(!refresh);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setStatusDialog({ open: false, id: "", currentStatus: "" });
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Referral code copied!");
  };

  const columns = [
    {
      name: "S.No.",
      width: "90px",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Name",
      selector: (row) => renderEllipsisText(row.name),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Referral Code",
      selector: (row) => (
        <Box display="flex" alignItems="center">
          <Typography fontWeight="bold" color="primary" sx={{ mr: 1 }}>
            {row?.referralCode}
          </Typography>
          <Tooltip title="Copy Referral Code">
            <IconButton size="small" onClick={() => handleCopy(row.referralCode)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      sortable: true,
    },
    {
      name: "Wallet Bids",
      selector: (row) => row?.totalBids,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === "active"}
                onClick={() =>
                  setStatusDialog({
                    open: true,
                    id: row._id,
                    currentStatus: row.status,
                  })
                }
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      width: "240px",
      cell: (row) => (
        <>
          <Tooltip title="View">
            <IconButton onClick={() => { setModalShow(true); setViewId(row._id); }}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => navigate(`/user-management/edit/${row._id}`)}>
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refer User">
            <IconButton onClick={() => navigate(`/user-management/refer-list/${row._id}`)}>
              <ListAlt />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => deleteUser(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="User Management" />
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" p={2}>
        <Box display="flex" gap={2}>
          <TableFilter
            data={STATUS_OPTIONS_LIST}
            value={status}
            setValue={setStatus}
            filterName="Status"
          />
          <Input
            value={searchItem}
            placeholder="Search by name or email"
            onChange={handleSearchChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => { setSearchItem(""); fetchUsers(""); }}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>
        <Button variant="contained" onClick={() => navigate("/user-management/add")}>
          Add New User
        </Button>
      </Box>

      <CardContent>
        <DataTable
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          onChangeRowsPerPage={(rows) => setPageSize(rows)}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationRowsPerPageOptions={[10, 20, 50]}
          customStyles={{
            headCells: {
              style: { fontWeight: "bold", fontSize: "14px" },
            },
          }}
        />
      </CardContent>

      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false })}>
        <DialogTitle>
          Are you sure you want to{" "}
          {statusDialog.currentStatus === "active" ? "deactivate" : "activate"} this user?
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={toggleStatus} color="primary">
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setStatusDialog({ open: false })}>
            No
          </Button>
        </DialogActions>
      </Dialog>

      {modalShow && (
        <ViewUserManagement show={modalShow} onHide={() => setModalShow(false)} id={viewId} />
      )}
    </Card>
  );
}
